import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ProfilePage.css';
import { useAuth } from '../context/AuthContext';
import { signOutUser, getUserProfile, updateUserBrugernavn, getUserIconPath, User } from '../services/userService';
import Footer from '../components/Footer';
import FriendsListModal from '../components/FriendsListModal';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const [userIconPath, setUserIconPath] = useState<string>('/images/User_icon.png');
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditingBrugernavn, setIsEditingBrugernavn] = useState(false);
  const [brugernavnValue, setBrugernavnValue] = useState('');
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);

  useEffect(() => {
    // If no user is logged in, redirect to login page
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Get the user's first name
    if (currentUser.displayName) {
      const firstName = currentUser.displayName.split(' ')[0];
      setUserName(firstName);
    }

    // Fetch user profile from Firestore
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setUserProfile(profile);
          setBrugernavnValue(profile.brugernavn || '');
          setUserIconPath(getUserIconPath(profile.userIcon));
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, navigate]);

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleLogoClick = () => {
    if (currentUser) {
      navigate('/main');
    }
  };

  const handleEditBrugernavn = () => {
    setIsEditingBrugernavn(true);
  };

  const handleSaveBrugernavn = async () => {
    if (!currentUser) return;

    // Validate max 30 characters
    if (brugernavnValue.length > 30) {
      alert('Brugernavn må maksimalt være 30 tegn');
      return;
    }

    try {
      await updateUserBrugernavn(currentUser.uid, brugernavnValue);
      setIsEditingBrugernavn(false);
      // Update local state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          brugernavn: brugernavnValue
        });
      }
    } catch (error) {
      console.error('Error updating brugernavn:', error);
      alert('Fejl ved opdatering af brugernavn');
    }
  };

  const handleCancelEdit = () => {
    setBrugernavnValue(userProfile?.brugernavn || '');
    setIsEditingBrugernavn(false);
  };

  const handleDeleteAccount = () => {
    setShowDeleteMessage(true);
    setTimeout(() => {
      setShowDeleteMessage(false);
    }, 3000);
  };

  if (loading) {
    return (
      <div className="profile-page fade-in">
        <div className="main-header">
          <div className="main-logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img src="/images/logo.png" alt="Wiin Logo" className="main-logo" />
          </div>
          <div className="main-profile-info">
          <div className="main-greeting-container">
            <h2 className="main-greeting">Hej {userName}!</h2>
            <img 
              src={userIconPath} 
              alt="User Avatar" 
              className="main-avatar"
            />
          </div>
          <p className="profile-logout-link" onClick={handleLogout}>Log ud</p>
        </div>
      </div>
        <div className="loading-profile">
          <div className="spinner"></div>
          <p>Indlæser profil...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="profile-page fade-in">
      {/* Header */}
      <div className="main-header">
        <div className="main-logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src="/images/logo.png" alt="Wiin Logo" className="main-logo" />
        </div>
        <div className="main-profile-info">
          <div className="main-greeting-container">
            <h2 className="main-greeting">Hej {userName}!</h2>
            <img 
              src={userIconPath} 
              alt="User Avatar" 
              className="main-avatar"
            />
          </div>
          <p className="profile-logout-link" onClick={handleLogout}>Log ud</p>
        </div>
      </div>

      {/* Profile Content */}
      <div className="profile-content">
        {/* Avatar Section */}
        <div className="profile-avatar-section">
          <img 
            src={userIconPath} 
            alt="User Avatar" 
            className="profile-avatar-large"
          />
        </div>

        {/* Profile Info Container */}
        <div className="profile-info-container">
          {/* Wiin ID - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Wiin ID</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.Wiin_ID || '-'}</span>
            </div>
          </div>

          {/* Brugernavn - Editable */}
          <div className="profile-field">
            <label className="profile-field-label">Brugernavn</label>
            <div className="profile-field-value-row">
              {isEditingBrugernavn ? (
                <>
                  <input
                    type="text"
                    value={brugernavnValue}
                    onChange={(e) => setBrugernavnValue(e.target.value)}
                    maxLength={30}
                    className="profile-field-input"
                    autoFocus
                  />
                  <button onClick={handleSaveBrugernavn} className="profile-save-btn">
                    ✓
                  </button>
                  <button onClick={handleCancelEdit} className="profile-cancel-btn">
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <span className="profile-field-value">
                    {userProfile?.brugernavn || 'Ingen brugernavn'}
                  </span>
                  <button onClick={handleEditBrugernavn} className="profile-edit-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Fornavn - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Fornavn</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.fornavn || '-'}</span>
            </div>
          </div>

          {/* Efternavn - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Efternavn</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.efternavn || '-'}</span>
            </div>
          </div>

          {/* Antal af spil - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Gennemført spil</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.antal_af_spil ?? 0}</span>
            </div>
          </div>

          {/* Antal vundne præmier - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Antal vundne præmier</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.antal_vundne_praemier ?? 0}</span>
            </div>
          </div>

          {/* Brugte præmier - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Brugte præmier</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.antal_brugte_praemier ?? 0}</span>
            </div>
          </div>

          {/* Præmie givet ud (Prizes shared to friends) - Read Only */}
          <div className="profile-field">
            <label className="profile-field-label">Præmier givet</label>
            <div className="profile-field-value-row">
              <span className="profile-field-value">{userProfile?.antal_delte_praemier ?? 0}</span>
            </div>
          </div>

          {/* Friends Button */}
          <button onClick={() => setShowFriendsModal(true)} className="profile-friends-btn">
            Venner
          </button>

          {/* Delete Account Button */}
          <button onClick={handleDeleteAccount} className="profile-delete-btn">
            Slet min konto
          </button>

          {/* Delete Message */}
          {showDeleteMessage && (
            <div className="profile-delete-message">
              Kommer snart
            </div>
          )}
        </div>
      </div>

      {/* Footer Navigation */}
      <Footer />

      {/* Friends Modal */}
      <FriendsListModal
        isOpen={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
        currentUserUsername={userProfile?.brugernavn || `${userProfile?.fornavn} ${userProfile?.efternavn}` || ''}
        currentUserWiinId={userProfile?.Wiin_ID || ''}
      />
    </div>
  );
};

export default ProfilePage;
